import { Col, Image, Row, Tag, Collapse, Avatar } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiArrowUpRight, FiMapPin, FiChevronDown, FiChevronUp, FiClock } from "react-icons/fi";
import apiClient from "../../Actions/apiClient";
import AssetPath from "../../AssetPath/AssetPath";
import PageTitle from "../../Components/PageTitle/PageTitle";
import SocialShare from "../../Components/SocialShare/SocialShare";
import { sanitizeHTML } from "../../Util/Util";
import "./EventDetails.scss";
import "../Agenda/Agenda.scss";

export default function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all events and find the one matching the eventId
    apiClient.get("events/list").then(({ data }) => {
      if (data && data.result) {
        const selectedEvent = data.result.find(
          (event) => event._id === eventId || event.id === eventId,
        );
        if (selectedEvent) {
          console.log("Selected Event Data:", selectedEvent);
          console.log("Agendas:", selectedEvent.agendas);
          setEventData(selectedEvent);
        }
      }
      setLoading(false);
    });
  }, [eventId]);

  const imageUrl = eventData ? eventData.thumbnailUrl || "" : "";

  if (loading) {
    return <div className="text-center p-5">Loading event details...</div>;
  }

  if (!eventData) {
    return (
      <div className="text-center p-5">
        <p className="text-danger">Event not found</p>
        <button
          className="ant-btn ant-btn-primary mt-3"
          onClick={() => navigate("/events-and-registration")}
        >
          Back to Events
        </button>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        mainTitle={eventData.title}
        breadcrumbItems={[
          { name: "Events and Registration", path: "/events-and-registration" },
          { name: eventData.title, path: `/event-details/${eventId}` },
        ]}
        image={imageUrl || AssetPath.breadcrumb.eventandRegistration}
      />

      <section className="spacing bg-white">
        <div className="container">
          <button
            className="ant-btn ant-btn-secondary mb-3 d-flex align-items-center"
            onClick={() => navigate("/events-and-registration")}
          >
            <FiArrowLeft size={16} className="me-2" />
            Back to Events
          </button>

          <Row gutter={[30, 30]}>
            {/* Event Image */}
            <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
              <Image src={imageUrl} alt={eventData.title} className="w-100" />
            </Col>

            {/* Event Details */}
            <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
              <h1 className="title text-primary fw-bold mb-2">
                {eventData.title}
              </h1>
              <h3 className="text-black fst-italic font-2 mb-4">
                {eventData.subtitle}
              </h3>

              {/* Date and Time */}
              <div className="event-meta mb-4">
                <div className="meta-item mb-2">
                  <span className="label fw-bold">Date & Time:</span>
                  <span className="ms-2">
                    {eventData.eventDateStart && eventData.eventDateEnd ? (
                      <>
                        {new Date(eventData.eventDateStart).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}{" "}
                        -{" "}
                        {new Date(eventData.eventDateEnd).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                        {eventData.eventTime && ` at ${eventData.eventTime}`}
                      </>
                    ) : (
                      eventData.eventDateStart &&
                      new Date(eventData.eventDateStart).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "2-digit",
                        },
                      )
                    )}
                  </span>
                </div>
              </div>

              {/* Location */}
              <div className="event-meta mb-4">
                <div className="meta-item d-flex align-items-start">
                  <FiMapPin size={20} className="text-primary me-2 mt-1" />
                  <div>
                    <span className="label fw-bold d-block">Location:</span>
                    <span>{eventData.location}</span>
                  </div>
                </div>
              </div>

              {/* Price */}
              {(eventData.priceinDollar || eventData.priceinRupees) && (
                <div className="event-meta mb-4">
                  <span className="label fw-bold">Price:</span>
                  <div className="d-flex gap-2 mt-2">
                    {eventData.priceinDollar && (
                      <Tag color="green">$ {eventData.priceinDollar}</Tag>
                    )}
                    {eventData.priceinRupees && (
                      <Tag color="green">₹ {eventData.priceinRupees}</Tag>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {eventData.shortDescription && (
              <div className="event-meta mb-4">
                <span className="label fw-bold d-block mb-2">Description:</span>
                <div 
                  className="text-gray"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(eventData.shortDescription)
                  }}
                />
              </div>
              )}

              {/* Full Description */}
              {eventData.fullDescription && (
                <div className="event-meta mb-4">
                  <span className="label fw-bold d-block mb-2">Details:</span>
                  <div 
                    className="text-gray"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(eventData.fullDescription)
                    }}
                  />
                </div>
              )}

              {/* Discount Code */}
              {eventData.discountCode && (
                <div className="event-meta mb-4 border-start border-primary ps-3">
                  <span className="label fw-bold d-block mb-2">
                    {eventData.discountLabel || "Discount Code"}:
                  </span>
                  <Tag color="blue" className="fs-5 py-2 px-3">
                    {eventData.discountCode}
                  </Tag>
                </div>
              )}

              {/* Action Buttons - Only show for Upcoming Events */}
              {eventData.eventType === "UpcomingEvents" && (
                <div className="buttons-group d-flex gap-2 flex-wrap mt-4">
                  {eventData.eventLink1 && (
                    <a
                      href={eventData.eventLink1}
                      className="ant-btn ant-btn-primary"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Register Now
                      <FiArrowUpRight size={16} className="ms-2" />
                    </a>
                  )}
                  {eventData.eventLink2 && (
                    <a
                      href={eventData.eventLink2}
                      className="ant-btn ant-btn-secondary"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Hotel Reservations
                      <FiArrowUpRight size={16} className="ms-2" />
                    </a>
                  )}
                </div>
              )}

              {/* Social Share */}
              <div className="mt-5 pt-4 border-top">
                <span className="label fw-bold d-block mb-3">Share Event:</span>
                <SocialShare />
              </div>
            </Col>
          </Row>

          {/* Agenda Items */}
          {eventData.agendas && eventData.agendas.length > 0 ? (
            <Row gutter={[30, 30]} className="mt-5 agenda">
              <Col xs={{ span: 24 }}>
                <h2 className="title text-primary fw-bold mb-4">Agenda</h2>
                <Collapse
                  expandIconPosition="right"
                  expandIcon={({ isActive }) =>
                    isActive ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />
                  }
                  items={eventData.agendas.map((agenda, agendaIndex) => ({
                    key: agendaIndex,
                    label: (
                      <span className="text-uppercase text-primary">
                        <span>Day {agendaIndex + 1}:</span>
                        <span className="ms-3 fw-bold">
                          {agenda.agendaMainTitle || "Agenda"}
                        </span>
                      </span>
                    ),
                    className: "custom-panel",
                    children: (
                      <div className="agenda-list">
                        {agenda.agendaItems && agenda.agendaItems.length > 0 ? (
                          agenda.agendaItems.map((item, index) => (
                            <div className="list" key={index}>
                              <div className="details d-flex flex-column flex-md-row align-items-left justify-content-between border-top">
                                <div className="time d-flex align-items-center">
                                  <FiClock size={16} className="text-primary me-1" />
                                  <span className="text-gray">{item.menuTime}</span>
                                </div>

                                <div className="notes">
                                  <div className="title-desc">
                                    <h6
                                      className="font-weight-bold text-primary text-uppercase mt-2 mb-1"
                                      style={{ lineHeight: "150%" }}
                                    >
                                      {item.menuTitle}
                                    </h6>
                                    {item.menuDesc && (
                                      <div
                                        className="agenda-description d-block text-gray"
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeHTML(item.menuDesc),
                                        }}
                                      />
                                    )}
                                  </div>

                                  {item.speaker && (
                                    <div className="speaker-details d-flex align-items-center mt-2">
                                      <div
                                        className="speaker-info d-block text-gray"
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeHTML(item.speaker),
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray">Agenda details coming soon.</p>
                        )}
                      </div>
                    ),
                  }))}
                />
              </Col>
            </Row>
          ) : (
            <Row gutter={[30, 30]} className="mt-5">
              <Col xs={{ span: 24 }}>
                <p className="text-gray">No agenda items available for this event</p>
              </Col>
            </Row>
          )}

          {/* Speakers */}
          {eventData.speakers && eventData.speakers.length > 0 && (
            <Row gutter={[30, 30]} className="mt-5">
              <Col xs={{ span: 24 }}>
                <h2 className="title text-primary fw-bold mb-4">Speakers</h2>
                <Row gutter={[20, 20]}>
                  {eventData.speakers.map((speaker, index) => (
                    <Col
                      key={index}
                      xs={{ span: 24 }}
                      sm={{ span: 12 }}
                      md={{ span: 8 }}
                    >
                      <div className="speaker-card text-center">
                        {speaker.image ? (
                          <Image
                            src={`${
                              process.env.REACT_APP_API_URL ||
                              "http://localhost:5004"
                            }${speaker.image}`}
                            alt={speaker.name}
                            className="speaker-image mb-3"
                          />
                        ) : <Avatar size={30} className="me-2" title={speaker.name} />}
                        <h4 className="fw-bold text-primary">{speaker.name}</h4>
                        <p className="text-gray font-2">{speaker.title}</p>
                        {speaker.bio && (
                          <p className="text-gray font-3">{speaker.bio}</p>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          )}
        </div>
      </section>
    </>
  );
}
